/**
 * Teachers Page
 * Manage teachers (admin only)
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Card } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Input } from '@/components/common/Input';
import { apiRequest } from '@/api/client';
import { useNotification } from '@/hooks/useNotification';

interface Teacher {
  teacherId: string;
  email: string;
  firstName: string;
  lastName: string;
  gradeLevel?: string;
  classLimit: number;
  classesCreated: number;
  isAdmin: boolean;
  isActive: boolean;
  verificationStatus: string;
  createdAt: string;
}

export const TeachersPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [pendingTeachers, setPendingTeachers] = useState<Teacher[]>([]);
  const [filteredTeachers, setFilteredTeachers] = useState<Teacher[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'active' | 'pending'>('active');

  useEffect(() => {
    if (!user?.isAdmin) {
      navigate('/dashboard');
      return;
    }
    fetchTeachers();
  }, []);

  useEffect(() => {
    const teachersToFilter = activeTab === 'active' ? teachers : pendingTeachers;
    if (searchTerm) {
      setFilteredTeachers(
        teachersToFilter.filter(t =>
          t.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          t.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          t.email.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    } else {
      setFilteredTeachers(teachersToFilter);
    }
  }, [searchTerm, teachers, pendingTeachers, activeTab]);

  const fetchTeachers = async () => {
    try {
      setIsLoading(true);
      
      // Get all school teachers
      const response = await apiRequest<{ teachers: Teacher[] }>('get_school_teachers', {
        adminEmail: user?.email,
      });
      
      if (response.success && (response as any).teachers) {
        const allTeachers = (response as any).teachers;
        setTeachers(allTeachers.filter((t: Teacher) => t.verificationStatus === 'approved'));
        setPendingTeachers(allTeachers.filter((t: Teacher) => t.verificationStatus === 'pending'));
      }
    } catch (error) {
      console.error('Failed to fetch teachers:', error);
      showNotification('error', 'Failed to load teachers');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApproveTeacher = async (teacher: Teacher) => {
    try {
      const response = await apiRequest('approve_teacher', {
        adminEmail: user?.email,
        teacherEmail: teacher.email,
        classLimit: 5, // Default class limit
      });

      if (response.success) {
        showNotification('success', `${teacher.firstName} ${teacher.lastName} has been approved`);
        fetchTeachers();
      } else {
        showNotification('error', 'Failed to approve teacher');
      }
    } catch (error) {
      console.error('Failed to approve teacher:', error);
      showNotification('error', 'Failed to approve teacher');
    }
  };

  const handleRejectTeacher = async (teacher: Teacher) => {
    try {
      const response = await apiRequest('reject_teacher', {
        adminEmail: user?.email,
        teacherEmail: teacher.email,
      });

      if (response.success) {
        showNotification('success', `${teacher.firstName} ${teacher.lastName} has been rejected`);
        fetchTeachers();
      } else {
        showNotification('error', 'Failed to reject teacher');
      }
    } catch (error) {
      console.error('Failed to reject teacher:', error);
      showNotification('error', 'Failed to reject teacher');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Teachers</h1>
          <p className="text-slate-600 mt-2">Manage teacher accounts and permissions</p>
        </div>
        <Button variant="primary" onClick={() => navigate('/teachers/new')}>
          ➕ Add Teacher
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 bg-white rounded-xl border border-slate-200 p-1 w-fit">
        <button
          onClick={() => setActiveTab('active')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'active'
              ? 'bg-emerald-600 text-white'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          Active Teachers ({teachers.length})
        </button>
        <button
          onClick={() => setActiveTab('pending')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'pending'
              ? 'bg-amber-600 text-white'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          Pending Approval ({pendingTeachers.length})
        </button>
      </div>

      {/* Search */}
      <Card>
        <div className="p-4">
          <Input
            type="text"
            placeholder="Search teachers by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </Card>

      {/* Teachers List */}
      {filteredTeachers.length === 0 ? (
        <Card>
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">👨‍🏫</span>
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              {searchTerm ? 'No teachers found' : activeTab === 'pending' ? 'No pending approvals' : 'No teachers yet'}
            </h3>
            <p className="text-slate-600 mb-6">
              {searchTerm
                ? 'Try adjusting your search'
                : activeTab === 'pending'
                ? 'All teacher accounts have been approved'
                : 'Add teachers to your school to get started'}
            </p>
            {!searchTerm && activeTab === 'active' && (
              <Button variant="primary" onClick={() => navigate('/teachers/new')}>
                Add Your First Teacher
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                    Teacher
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                    Classes
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {filteredTeachers.map((teacher) => (
                  <tr key={teacher.teacherId} className="hover:bg-slate-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                          <span className="text-emerald-600 font-semibold">
                            {teacher.firstName[0]}{teacher.lastName[0]}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-slate-900">
                            {teacher.firstName} {teacher.lastName}
                          </div>
                          {teacher.gradeLevel && (
                            <div className="text-sm text-slate-500">
                              Year {teacher.gradeLevel}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-900">{teacher.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-900">
                        {teacher.classesCreated} / {teacher.classLimit}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {teacher.isAdmin ? (
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-lg">
                          Admin
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-slate-100 text-slate-700 text-xs font-medium rounded-lg">
                          Teacher
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {activeTab === 'pending' ? (
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRejectTeacher(teacher)}
                          >
                            Reject
                          </Button>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleApproveTeacher(teacher)}
                          >
                            Approve
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/teachers/${teacher.teacherId}`)}
                        >
                          View
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};
